import { useState, useCallback, useEffect } from "react";
import { useGeneralMessage } from "../../common/APICommon";
import { SetAuditTrail } from "../../components/Reports/AuditTrailReportEmployeeSetup";
import employeeTransactionApproverService from "../services/employeeTransactionApproverService";

export const useEmployeeTransactionApprover = (user) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [filterData, setFilterData] = useState([]);
  const { showGeneralMessage, showMessage, message, isSuccess } = useGeneralMessage();

  const fetchApprovers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await employeeTransactionApproverService.getAll();
      const responseData = res.data || [];
      setData(responseData);
      setFilterData(responseData);
    } catch (err) {
      console.error("Failed to fetch approvers", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApprovers();
  }, [fetchApprovers]);

  // MODIFIED: Helper to ensure Audit Trail doesn't break the UI if it fails
  const safeAudit = async (auditData) => {
    try {
      await SetAuditTrail(auditData);
    } catch (err) {
      console.warn("Audit Trail failed, but action succeeded:", err);
    }
  };

  const addApprover = async (employeeId, fullName) => {
    setLoading(true);
    try {
      const payload = { employeePersonalInformationId: employeeId, createdById: user.id };
      await employeeTransactionApproverService.create(payload);

      // MODIFIED: Move Audit to safe wrapper
      await safeAudit({
        setupArea: "Employee Transaction Approver",
        actionTaken: "insert",
        field: "Add Approver",
        newValue: fullName,
        oldValue: "-",
        dateSaved: new Date(),
        savedById: user.id
      });

      showGeneralMessage("Approver added successfully", true);
      await fetchApprovers();
      return { success: true };
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Failed to add approver";
      showGeneralMessage(errorMsg, false);
      console.error("Error adding approver:", err);
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  // ADDED: Update Approver Logic
  const updateApprover = async (id, employeeId, fullName, oldName) => {
    setLoading(true);
    try {
      const payload = { employeePersonalInformationId: employeeId, createdById: user.id };
      await employeeTransactionApproverService.update(id, payload);

      await safeAudit({
        setupArea: "Employee Transaction Approver",
        actionTaken: "update",
        field: "Change Approver",
        newValue: fullName,
        oldValue: oldName,
        dateSaved: new Date(),
        savedById: user.id
      });

      showGeneralMessage("Approver updated successfully", true);
      await fetchApprovers();
      return { success: true };
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Failed to update approver";
      showGeneralMessage(errorMsg, false);
      console.error("Error updating approver:", err);
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const removeApprover = async (id, fullName) => {
    setLoading(true);
    try {
      await employeeTransactionApproverService.delete(id);

      await safeAudit({
        setupArea: "Employee Transaction Approver",
        actionTaken: "delete",
        field: "Remove Approver",
        newValue: "-",
        oldValue: fullName,
        dateSaved: new Date(),
        savedById: user.id
      });

      showGeneralMessage("Approver removed successfully", true);
      await fetchApprovers();
    } catch (err) {
      showGeneralMessage("Failed to remove approver", false);
    } finally {
      setLoading(false);
    }
  };

  const searchData = (value) => {
    if (!value) {
      setData(filterData);
      return;
    }
    const lower = value.toLowerCase();
    setData(filterData.filter(f =>
      f.fullName?.toLowerCase().includes(lower) ||
      f.employeeNo?.toLowerCase().includes(lower)
    ));
  };

  return {
    loading,
    data,
    showMessage,
    message,
    isSuccess,
    searchData,
    addApprover,
    updateApprover, // ADDED
    removeApprover
  };
};